import { Component, Inject, ViewChild } from '@angular/core';
import { FormBuilder, FormControl, FormGroup, FormGroupDirective, NgForm, Validators } from '@angular/forms';
import { MAT_DIALOG_DATA, MatDialog, MatDialogRef} from '@angular/material/dialog';
import {DateAdapter, ErrorStateMatcher} from '@angular/material/core';
import { OrganisationService } from '../organisation.service';
import { Backup, MAX_BACKUPS, MODEL_DESCRIPTION_MAX, MODEL_FUNCTION, MODEL_NAME_MAX, MODEL_NAME_MIN, ModelDetails, Project } from '../../common/organisation';
import { UserAccessComponent } from '../users/user-access.component';
import { MatTableDataSource } from '@angular/material/table';
import { CURRENT_STATE_PROJECT_KEY } from '../../common/linqset-model';
import { User, USER_ROLE } from '../../common/user';
import { BackupService } from '../backups/backup.service';
import { ConfirmDialogComponent, ConfirmDialogModel } from '../../ui-components/dialogs/confirm-dialog.component';
import { lastValueFrom } from 'rxjs';

/** Error when invalid control is dirty, touched, or submitted. */
export class MyErrorStateMatcher implements ErrorStateMatcher {
  isErrorState(control: FormControl | null, form: FormGroupDirective | NgForm | null): boolean {
    const isSubmitted = form && form.submitted;
    return !!(control && control.invalid && (control.dirty || control.touched || isSubmitted));
  }
}

@Component({
    selector: 'model-dialog',
    styleUrls: ['./model-dialog.css'],
    templateUrl: 'model-dialog.html',
    standalone: false
})
export class ModelDialogComponent {
    modelForm: FormGroup
    file: File
    matcher = new MyErrorStateMatcher();
    title:string
    subtitle:string
    functions:any[]
    model:ModelDetails
    project:Project
    turnOffUpload:boolean
    users:User[]

    projectKey:string
    MAX_BACKUPS = MAX_BACKUPS
    MODEL_NAME_MAX = MODEL_NAME_MAX
    MODEL_DESCRIPTION_MAX = MODEL_DESCRIPTION_MAX

    backupColumns: string[] = ['date', 'description', 'restore', 'delete'];

    backupDataSource:MatTableDataSource<Backup> = new MatTableDataSource<Backup>()

    @ViewChild(UserAccessComponent) userAccess: UserAccessComponent

    constructor(public dialogRef: MatDialogRef<ModelDialogComponent>, private formBuilder: FormBuilder, private organisationService:OrganisationService,
        private backupService:BackupService, private readonly adapter: DateAdapter<Date>, private dialog:MatDialog,
        @Inject(MAT_DIALOG_DATA) public data: {model:ModelDetails, project:Project, isNew:boolean, turnOffUpload?:boolean}) {

        this.adapter.setLocale("en-NZ");

        this.model = this.data.model
        this.project = this.data.project
        this.turnOffUpload = data.turnOffUpload

        this.modelForm  =  this.formBuilder.group({
            name: [this.model.name, [Validators.required, Validators.minLength(MODEL_NAME_MIN), Validators.maxLength(MODEL_NAME_MAX)]],
            description: [this.model.description, Validators.maxLength(MODEL_DESCRIPTION_MAX)],
            useOrganisationAccess: [this.model.useOrganisationAccess],
            function: [this.model.function, [Validators.required]],
            tagKeys: [this.model.tagKeys],
            owner: [this.model.owner],
            reviewDate: [this.model.reviewDate]
        })

        this.projectKey = this.project?this.project._key:CURRENT_STATE_PROJECT_KEY
        this.setBackupsDataSource()

        this.title = data.isNew ? "Create model" : "Edit model"
        this.subtitle = this.project ? `In the project ${this.project.name}` : "In current state"

        this.users = this.organisationService.users || []

        this.functions = Object.values(MODEL_FUNCTION)
    }

    setBackupsDataSource() {
        const projectBackups = this.model.backups?this.model.backups.filter(a => a.projectKey === this.projectKey).sort((a,b) => {return a.date > b.date?-1:1}):[]
        this.backupDataSource.data = projectBackups
    }

    onFileSelected(event) {
        this.file = event.target.files[0];
        event.target.value = ''     // lets the same file be chosen again after clearing it
    }

    clearFile() {
        this.file = undefined
    }

    getNameError() {
        return `Name must be between ${MODEL_NAME_MIN} and ${MODEL_NAME_MAX} characters`
    }

    getDescriptionError() {
        return `Description must be less than ${MODEL_DESCRIPTION_MAX} characters`
    }

    getModelTags() {
        return this.organisationService.organisation.modelTags?this.organisationService.organisation.modelTags.tags:[]
    }

    async deleteBackup(backup:Backup) {
        const dialogData = new ConfirmDialogModel("Delete backup", [`Permanently delete the backup from ${new Date(backup.date).toLocaleString()}? This can't be undone.`], undefined, false, "Delete", "Cancel")
        const result = await lastValueFrom(this.dialog.open(ConfirmDialogComponent, { maxWidth: "400px", data: dialogData }).afterClosed())
        if (result?.button !== "Delete") return

        await this.backupService.deleteBackup(this.organisationService.organisation, this.model._key, backup._key, this.project?._key)
        this.model.backups = this.model.backups.filter(a => a._key !== backup._key)
        this.setBackupsDataSource()
    }

    // restoring replaces the whole model, so close without saving and let the restore confirm itself
    restoreBackup(backup:Backup) {
        this.dialogRef.close()
        this.backupService.confirmRestoreBackup(this.organisationService.organisation, this.model, this.project, backup._key)
    }

    hasBackups() {
        return this.backupDataSource.data.length > 0
    }

    // closes with an updated copy; the model itself only changes once it has been saved
    save() {
        this.modelForm.markAllAsTouched()
        const accessValid = this.modelForm.value.useOrganisationAccess || !this.userAccess || this.userAccess.isValid()
        if (!accessValid || this.modelForm.invalid) return

        const values = this.modelForm.value
        const model:ModelDetails = {
            ...this.model,
            name: values.name.trim(),
            description: values.description || '',
            useOrganisationAccess: values.useOrganisationAccess,
            role: values.useOrganisationAccess ? this.organisationService.organisation.role : USER_ROLE.ADMIN,
            function: values.function,
            tagKeys: values.tagKeys,
            owner: values.owner,
            reviewDate: values.reviewDate,
        }
        if (this.userAccess) model.userAccess = this.userAccess.getUserAccess()

        this.dialogRef.close({file:this.file, model});
    }
}
